import { useState, useEffect, type JSX } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import { InputSwitch } from 'primereact/inputswitch'
import { Password } from 'primereact/password'
import { Dropdown } from 'primereact/dropdown'
import { api } from '../../../api/client.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import EmailOAuthSignInButtons, { type EmailOAuthMessage } from '../../../components/admin/channels/EmailOAuthSignInButtons.js'
import TestEmailDrawer from '../../../components/admin/channels/TestEmailDrawer.js'
import type { EmailOutboundConfig } from './EmailOutboundPage.js'
import { Plus, X } from 'lucide-react'
import {
  FormContainer,
  FormGrid,
  FormField,
  FormDivider,
  StepperSection,
  StickyFooter,
  type StepItem,
} from '../../../components/forms/layout/index.js'

interface MonitorRule {
  id: string
  to: string
  cc: string
  campaign: string
}

interface Campaign {
  id: string
  name: string
  dialer_type: string
}

export default function EmailOutboundFormPage(): JSX.Element {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [activeTab, setActiveTab] = useState(0)
  const [monitorRules, setMonitorRules] = useState<MonitorRule[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [isViewMode, setIsViewMode] = useState(false)
  const [hasStoredPassword, setHasStoredPassword] = useState(false)
  const [testDrawerOpen, setTestDrawerOpen] = useState(false)
  const [formData, setFormData] = useState({
    status: true,
    isDefault: false,
    name: '',
    description: '',
    smtpHost: '',
    smtpPort: 587,
    email: '',
    authEnabled: false,
    oauthProvider: '' as 'google' | 'microsoft' | '',
    password: '',
    connectionTimeout: 30,
    authTimeout: 30,
    encryptionEnabled: false,
    deleteAfterFetch: false,
    markSeen: false,
    ignoreCc: false,
  })

  const isEditMode = id && id !== 'new' && !isViewMode

  useEffect(() => {
    setIsViewMode(searchParams.get('view') === 'true')
  }, [searchParams])

  useEffect(() => {
    const loadData = async () => {
      const campaignsData = await fetchCampaigns()
      if (id && id !== 'new') {
        await fetchConfig(campaignsData)
      }
    }
    void loadData()
  }, [id])

  const fetchCampaigns = async () => {
    try {
      const data = await api.get<{ campaigns: Campaign[] }>('/campaigns/dropdown?category=EMAIL')
      setCampaigns(data.campaigns)
      return data.campaigns
    } catch {
      toast.error('Failed to fetch campaigns')
      return []
    }
  }

  const fetchConfig = async (_campaignsData: Campaign[] = []) => {
    setLoading(true)
    try {
      const data = await api.get<EmailOutboundConfig>(`/admin/channels/email/outbound/${id}`)
      setFormData({
        status: data.status === 'ACTIVE',
        isDefault: data.isDefault,
        name: data.name || '',
        description: data.description || '',
        smtpHost: data.smtpHost,
        smtpPort: data.smtpPort,
        email: data.smtpUser,
        authEnabled: data.authEnabled || false,
        oauthProvider: (data.oauthProvider as 'google' | 'microsoft' | '') || '',
        password: '',
        connectionTimeout: data.connectionTimeout || 30,
        authTimeout: data.authTimeout || 30,
        encryptionEnabled: data.encryptionEnabled || false,
        deleteAfterFetch: data.deleteAfterFetch || false,
        markSeen: data.markSeen || false,
        ignoreCc: data.ignoreCc || false,
      })
      setHasStoredPassword(data.hasPassword)
      // Set monitor rules if available
      if (data.monitorRules && Array.isArray(data.monitorRules)) {
        const mappedRules = data.monitorRules.map((r: { id: string; to: string; cc: string | null; campaign: string }) => ({
          id: r.id,
          to: r.to,
          cc: r.cc || '',
          campaign: r.campaign,
        }))
        setMonitorRules(mappedRules)
      }
    } catch {
      toast.error('Failed to fetch email outbound config')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Client-side validation (only for password-based saves, not OAuth)
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.smtpHost.trim()) {
      newErrors.smtpHost = 'SMTP Host is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    // Password only required for non-OAuth, non-edit saves
    if (!isEditMode && !formData.authEnabled && !formData.password.trim()) {
      newErrors.password = 'Password is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      setActiveTab(0)
      toast.error('Please fix the validation errors')
      return
    }

    setErrors({})
    setLoading(true)
    try {
      const payload: Record<string, unknown> = {
        status: formData.status ? 'ACTIVE' : 'DISABLED',
        isDefault: formData.isDefault,
        name: formData.name.trim() || null,
        description: formData.description.trim() || null,
        smtpHost: formData.smtpHost,
        smtpPort: formData.smtpPort,
        smtpUser: formData.email,
        fromAddress: formData.email,
        fromName: formData.name.trim() || null,
        authEnabled: formData.authEnabled,
        oauthProvider: formData.oauthProvider || null,
        connectionTimeout: formData.connectionTimeout,
        authTimeout: formData.authTimeout,
        encryptionEnabled: formData.encryptionEnabled,
        deleteAfterFetch: formData.deleteAfterFetch,
        markSeen: formData.markSeen,
        ignoreCc: formData.ignoreCc,
        monitorRules: monitorRules
          .filter((r) => r.to.trim() && r.campaign.trim())
          .map((r) => ({
            to: r.to,
            cc: r.cc,
            campaign: r.campaign,
          })),
      }
      // Only include password for non-OAuth flows
      if (formData.password && !formData.authEnabled) {
        payload.smtpPassword = formData.password
      }

      if (isEditMode) {
        await api.put(`/admin/channels/email/outbound/${id}`, payload)
        toast.success('Email outbound config updated')
      } else {
        await api.post('/admin/channels/email/outbound', payload)
        toast.success('Email outbound config created')
      }
      navigate('/admin/channels/email/outbound')
    } catch {
      toast.error('Failed to save email outbound config')
    } finally {
      setLoading(false)
    }
  }

  const validateField = (field: string, value: string): string | null => {
    if (field === 'name' && !value.trim()) return 'Name is required'
    if (field === 'smtpHost' && !value.trim()) return 'SMTP Host is required'
    if (field === 'email' && !value.trim()) return 'Email is required'
    if (field === 'email' && value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format'
    if (field === 'password' && !isEditMode && !formData.authEnabled && !value.trim()) return 'Password is required'
    return null
  }

  const handleFieldChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value })
    const error = validateField(field, value)
    setErrors((prev) => ({ ...prev, [field]: error || '' }))
  }

  const handleCancel = () => {
    navigate('/admin/channels/email/outbound')
  }

  const addMonitorRule = (): void => {
    setMonitorRules([...monitorRules, { id: `new-${Date.now()}-${monitorRules.length}`, to: '', cc: '', campaign: '' }])
  }

  const updateMonitorRule = (ruleId: string, field: keyof Omit<MonitorRule, 'id'>, value: string): void => {
    setMonitorRules(monitorRules.map((r) => (r.id === ruleId ? { ...r, [field]: value } : r)))
  }

  const removeMonitorRule = (ruleId: string): void => {
    setMonitorRules(monitorRules.filter((r) => r.id !== ruleId))
  }

  const canTestEmail =
    formData.smtpHost.trim() !== '' &&
    formData.smtpPort > 0 &&
    formData.email.trim() !== '' &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
    (formData.authEnabled || formData.password.trim() !== '' || hasStoredPassword)

  const handleOAuthComplete = (result: EmailOAuthMessage) => {
    if (id === 'new') {
      navigate(`/admin/channels/email/outbound/${result.configId}`)
    } else {
      void fetchConfig()
    }
    toast.success(`${result.provider === 'google' ? 'Google' : 'Microsoft'} account connected`)
  }

  const title = isViewMode ? 'View Email Outbound Config' : isEditMode ? 'Edit Email Outbound Config' : 'Add Email Outbound Config'
  const description = isViewMode
    ? 'View SMTP outbound email configuration details.'
    : isEditMode
      ? 'Edit SMTP outbound email configuration.'
      : 'Configure a new SMTP outbound email provider.'

  if (loading && id && id !== 'new') {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="pi pi-spinner pi-spin text-3xl text-[var(--kc3-accent)]" />
          <span className="text-sm font-medium text-gray-600">Loading email outbound config...</span>
        </div>
      </div>
    )
  }

  const bodyClass = `space-y-5 p-6 ${isViewMode ? 'pointer-events-none' : ''}`

  const panelHeader = (icon: string, eyebrow: string, headingTitle: string, headingDescription: string): JSX.Element => (
    <div className="border-b border-gray-100 px-6 py-5">
      <div className="flex items-start gap-3.5">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--kc3-accent)]/10 text-[var(--kc3-accent)]">
          <i className={`${icon} text-lg`} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--kc3-accent)]">{eyebrow}</span>
          <h2 className="mt-0.5 text-lg font-bold text-gray-900">{headingTitle}</h2>
          <p className="mt-1 text-sm text-gray-500">{headingDescription}</p>
        </div>
      </div>
    </div>
  )

  const tabs: StepItem[] = [
    {
      key: 'basic',
      step: 1,
      label: 'Basic Info',
      sublabel: 'Connection & Auth',
      content: (
        <div className="rounded-sm border border-gray-200 bg-white shadow-sm overflow-hidden">
          {panelHeader('pi pi-envelope', 'BASIC INFO', 'Basic Information', 'Name, connection details, and authentication.')}
          <div className={bodyClass}>
            {/* Status & Identification */}
            <FormGrid columns={2}>
              <FormField label="Status" htmlFor="status">
                <div className="flex items-center gap-2">
                  <InputSwitch
                    inputId="status"
                    checked={formData.status}
                    onChange={(e: { value?: boolean }) => setFormData({ ...formData, status: e.value ?? false })}
                    disabled={isViewMode}
                  />
                  <span className="text-sm text-gray-600">{formData.status ? 'Active' : 'Disabled'}</span>
                </div>
              </FormField>
              <FormField label="Set as Default" htmlFor="isDefault">
                <InputSwitch
                  inputId="isDefault"
                  checked={formData.isDefault}
                  onChange={(e: { value?: boolean }) => setFormData({ ...formData, isDefault: e.value ?? false })}
                  disabled={isViewMode}
                />
              </FormField>
            </FormGrid>

            <FormDivider />

            <FormGrid columns={2}>
              <FormField label="Name" htmlFor="name" required error={errors.name}>
                <InputText
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  className={`w-full ${errors.name ? 'p-invalid' : ''}`}
                  disabled={isViewMode}
                />
              </FormField>
              <FormField label="Description" htmlFor="description" error={errors.description}>
                <InputText
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  className={`w-full ${errors.description ? 'p-invalid' : ''}`}
                  disabled={isViewMode}
                />
              </FormField>
            </FormGrid>

            <FormDivider />

            {/* SMTP Connection Settings */}
            <FormGrid columns={2}>
              <FormField label="SMTP Host" htmlFor="smtpHost" required error={errors.smtpHost} help="SMTP Server. eg. smtp.server.com">
                <InputText
                  id="smtpHost"
                  value={formData.smtpHost}
                  onChange={(e) => handleFieldChange('smtpHost', e.target.value)}
                  className={`w-full ${errors.smtpHost ? 'p-invalid' : ''}`}
                  disabled={isViewMode}
                />
              </FormField>
              <FormField label="Port" htmlFor="smtpPort">
                <InputNumber
                  inputId="smtpPort"
                  value={formData.smtpPort}
                  onChange={(e) => setFormData({ ...formData, smtpPort: e.value === null ? 0 : e.value })}
                  className="w-full"
                  min={0}
                  max={65535}
                  disabled={isViewMode}
                />
              </FormField>
            </FormGrid>

            <FormGrid columns={2}>
              <FormField label="Encryption" htmlFor="encryptionEnabled">
                <InputSwitch
                  inputId="encryptionEnabled"
                  checked={formData.encryptionEnabled}
                  onChange={(e: { value?: boolean }) => setFormData({ ...formData, encryptionEnabled: e.value ?? false })}
                  disabled={isViewMode}
                />
              </FormField>
              <FormField label="Connection Timeout" htmlFor="connectionTimeout">
                <InputNumber
                  inputId="connectionTimeout"
                  value={formData.connectionTimeout}
                  onChange={(e) => setFormData({ ...formData, connectionTimeout: e.value ?? 30 })}
                  className="w-full"
                  min={1}
                  max={300}
                  disabled={isViewMode}
                />
              </FormField>
            </FormGrid>

            <FormField label="Authentication Timeout" htmlFor="authTimeout">
              <InputNumber
                inputId="authTimeout"
                value={formData.authTimeout}
                onChange={(e) => setFormData({ ...formData, authTimeout: e.value ?? 30 })}
                className="w-full"
                min={1}
                max={300}
                disabled={isViewMode}
              />
            </FormField>

            <FormDivider />

            {/* Authentication & From Address */}
            <FormField label="Auth" htmlFor="authEnabled">
              <InputSwitch
                inputId="authEnabled"
                checked={formData.authEnabled}
                onChange={(e: { value?: boolean }) => setFormData({ ...formData, authEnabled: e.value ?? false })}
                disabled={isViewMode}
              />
            </FormField>

            <FormField label="Email (From Address)" htmlFor="email" required error={errors.email}>
              <InputText
                id="email"
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                className={`w-full ${errors.email ? 'p-invalid' : ''}`}
                type="email"
                disabled={isViewMode}
              />
            </FormField>

            {!formData.authEnabled && (
              <FormField
                label="Password"
                htmlFor="password"
                required={!isEditMode}
                error={errors.password}
                help={isEditMode ? 'Leave blank to keep current password.' : undefined}
              >
                <Password
                  inputId="password"
                  value={formData.password}
                  onChange={(e) => handleFieldChange('password', e.target.value)}
                  className={`w-full ${errors.password ? 'p-invalid' : ''}`}
                  inputClassName="w-full"
                  feedback={false}
                  toggleMask
                  disabled={isViewMode}
                />
              </FormField>
            )}

            {formData.authEnabled && !isViewMode && (
              <FormField label="Sign in with" htmlFor="oauth">
                <EmailOAuthSignInButtons configId={id} channel="outbound" onOAuthComplete={handleOAuthComplete} />
              </FormField>
            )}

            <FormDivider />

            {/* Email Options */}
            <FormGrid columns={2}>
              <FormField label="Delete emails from server after fetching?" htmlFor="deleteAfterFetch">
                <InputSwitch
                  inputId="deleteAfterFetch"
                  checked={formData.deleteAfterFetch}
                  onChange={(e: { value?: boolean }) => setFormData({ ...formData, deleteAfterFetch: e.value ?? false })}
                  disabled={isViewMode}
                />
              </FormField>
              <FormField label="Mark Email Seen" htmlFor="markSeen">
                <InputSwitch
                  inputId="markSeen"
                  checked={formData.markSeen}
                  onChange={(e: { value?: boolean }) => setFormData({ ...formData, markSeen: e.value ?? false })}
                  disabled={isViewMode}
                />
              </FormField>
              <FormField label="Ignore CC" htmlFor="ignoreCc">
                <InputSwitch
                  inputId="ignoreCc"
                  checked={formData.ignoreCc}
                  onChange={(e: { value?: boolean }) => setFormData({ ...formData, ignoreCc: e.value ?? false })}
                  disabled={isViewMode}
                />
              </FormField>
            </FormGrid>
          </div>
        </div>
      ),
    },
    {
      key: 'monitor',
      step: 2,
      label: 'Monitor IDs and Set Campaign',
      sublabel: 'Monitor Rules',
      content: (
        <div className="rounded-sm border border-gray-200 bg-white shadow-sm overflow-hidden">
          {panelHeader('pi pi-eye', 'MONITORING', 'Monitor Rules', 'Route replies matching To/CC addresses to a campaign.')}
          <div className={bodyClass}>
            <FormField label="Monitor Rules" htmlFor="monitorRules">
              <div className="space-y-3">
                {monitorRules.map((rule) => (
                  <div key={rule.id} className="flex items-end gap-2 rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                    <div className="flex-1">
                      <label className="mb-1 block text-xs font-medium text-gray-600">To</label>
                      <InputText
                        value={rule.to}
                        onChange={(e) => updateMonitorRule(rule.id, 'to', e.target.value)}
                        className="w-full"
                        disabled={isViewMode}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="mb-1 block text-xs font-medium text-gray-600">CC</label>
                      <InputText
                        value={rule.cc}
                        onChange={(e) => updateMonitorRule(rule.id, 'cc', e.target.value)}
                        className="w-full"
                        disabled={isViewMode}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="mb-1 block text-xs font-medium text-gray-600">Campaign</label>
                      <Dropdown
                        value={rule.campaign}
                        onChange={(e) => updateMonitorRule(rule.id, 'campaign', e.value)}
                        options={campaigns}
                        optionLabel="name"
                        optionValue="id"
                        className="w-full"
                        placeholder="Select Campaign"
                        disabled={isViewMode}
                      />
                    </div>
                    {!isViewMode && (
                      <Button
                        type="button"
                        className="p-button-danger p-button-outlined"
                        onClick={() => removeMonitorRule(rule.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {!isViewMode && (
                  <Button type="button" className="p-button-outlined" onClick={addMonitorRule}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Monitor Rule
                  </Button>
                )}
                {monitorRules.length === 0 && isViewMode && (
                  <p className="text-sm text-gray-500">No monitor rules configured.</p>
                )}
              </div>
            </FormField>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <FormContainer className="w-full px-6 py-8">
        <div className="rounded-sm border border-gray-200 bg-white shadow-sm p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="inline-flex items-center rounded-full bg-[var(--kc3-accent)]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--kc3-accent)]">
                EMAIL OUTBOUND
              </span>
              <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">{title}</h1>
              <p className="mt-1 text-sm text-gray-500">{description}</p>
            </div>
            <div className="flex shrink-0 gap-3">
              {!isViewMode && canTestEmail && (
                <button
                  type="button"
                  onClick={() => setTestDrawerOpen(true)}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-5 py-2.5 text-sm font-semibold text-indigo-700 transition-all hover:bg-indigo-100 disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  <i className="pi pi-paper-plane" aria-hidden="true" />
                  Test Email
                </button>
              )}
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
              >
                {isViewMode ? 'Close' : 'Cancel'}
              </button>
              {!isViewMode && (
                <button
                  type="submit"
                  form="email-outbound-form"
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-[var(--kc3-accent)] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                >
                  {loading ? (
                    <>
                      <i className="pi pi-spinner pi-spin" aria-hidden="true" />
                      Saving…
                    </>
                  ) : isEditMode ? (
                    'Update'
                  ) : (
                    'Create'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        <form id="email-outbound-form" onSubmit={handleSubmit}>
          <StepperSection steps={tabs} activeIndex={activeTab} onChange={setActiveTab} />
        </form>

        {!isViewMode && (
          <StickyFooter
            icon="pi pi-envelope"
            eyebrow="You are editing"
            title={formData.name || title}
            closeLabel="Close"
            onClose={handleCancel}
            loading={loading}
          />
        )}

        <TestEmailDrawer
          visible={testDrawerOpen}
          onHide={() => setTestDrawerOpen(false)}
          mode="smtp"
          getDraftConfig={() => {
            const config: Record<string, unknown> = {
              id,
              smtpHost: formData.smtpHost,
              smtpPort: formData.smtpPort,
              smtpUser: formData.email,
              fromAddress: formData.email,
              encryptionEnabled: formData.encryptionEnabled,
            }
            // Only include password if it's not empty (for new configs or when user entered a new password)
            if (formData.password.trim()) {
              config.smtpPassword = formData.password
            }
            return config
          }}
          testEndpoint="/admin/channels/email/outbound/test"
        />
      </FormContainer>
    </div>
  )
}
